import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { userAtom } from "../../recoil/userAtom";
import { useRecoilValue } from "recoil";
import useIsInappWebview from "../../hooks/useIsInappWebview";
import useMixpanelWeb from "../../hooks/useMixpanelWeb";
import { useOnboardingProgress } from "../../hooks/useOnboardingProgress";
import FullPageWithCenterContentV2 from "../container/FullPageWithCenterContentV2";
import FullPageWithHeaderAndProgress from "../container/FullPageWithHeaderAndProgress";
import { theme } from "../../styles/theme";
import useClose from "../../hooks/useClose";
import { useMutation } from "@tanstack/react-query";
import { programSetupApi } from "../../api/programSetupApi";
import { ProgramSetup_2_Load } from "./part/load/ProgramSetup_2_Load";
import Convention_PS_1 from "./part/convention/Convention_PS_1";
import Convention_PS_2_QnA from "./part/convention/Convention_PS_2_QnA";
import Convention_PS_3 from "./part/convention/Convention_PS_3";
import Habit_PS_1 from "./part/habit/Habit_PS_1";
import Habit_PS_2_QnA from "./part/habit/Habit_PS_2_QnA";
import Habit_PS_3 from "./part/habit/Habit_PS_3";
import Habit_PS_4 from "./part/habit/Habit_PS_4";
import Habit_PS_5 from "./part/habit/Habit_PS_5";
import Habit_PS_6 from "./part/habit/Habit_PS_6";
import Habit_PS_7 from "./part/habit/Habit_PS_7";
import Motivation_PS_1 from "./part/motivation/Motivation_PS_1";
import Motivation_PS_2 from "./part/motivation/Motivation_PS_2";
import Motivation_PS_3 from "./part/motivation/Motivation_PS_3";
import Motivation_PS_4 from "./part/motivation/Motivation_PS_4";
import Motivation_PS_5 from "./part/motivation/Motivation_PS_5";
import Motivation_PS_6 from "./part/motivation/Motivation_PS_6";
import Motivation_PS_7_MultiSelect from "./part/motivation/Motivation_PS_7_MultiSelect";
import Motivation_PS_8 from "./part/motivation/Motivation_PS_8";

interface ProgramSetupStep {
  type: string;
  seqByType: number;
  title?: string;
  hideHeader?: boolean;
  hideProgress?: boolean;
  disableBack?: boolean;
  hideBack?: boolean;
  hideClose?: boolean;
  progressDelta?: number;
  centerFullScreen?: boolean;
  backgroundColor?: string;
  paddingXContent?: number;
  content: React.ReactNode;
}

const commonSteps: ProgramSetupStep[] = [
  {
    type: "load",
    seqByType: 1,
    hideHeader: true,
    hideProgress: true,
    content: <ProgramSetup_2_Load />,
  },
  {
    type: "convention",
    seqByType: 1,
    title: "인식과 기대",
    disableBack: true,
    progressDelta: 1 / 12,
    content: <Convention_PS_1 />,
  },
  {
    type: "convention",
    seqByType: 2,
    title: "인식과 기대",
    progressDelta: 1 / 12,
    content: <Convention_PS_2_QnA />,
  },
  {
    type: "convention",
    seqByType: 3,
    hideProgress: true,
    backgroundColor: theme.vars.palette.Background.SoftHighlight,
    content: <Convention_PS_3 />,
  },
  {
    type: "habit",
    seqByType: 1,
    title: "습관과 노력",
    progressDelta: 1 / 7,
    content: <Habit_PS_1 />,
  },
  {
    type: "habit",
    seqByType: 2,
    title: "습관과 노력",
    progressDelta: 1 / 7,
    content: <Habit_PS_2_QnA />,
  },
  {
    type: "habit",
    seqByType: 3,
    hideProgress: true,
    content: <Habit_PS_3 />,
  },
  {
    type: "habit",
    seqByType: 4,
    hideBack: true,
    hideProgress: true,
    content: <Habit_PS_4 />,
  },
  {
    type: "habit",
    seqByType: 5,
    hideProgress: true,
    backgroundColor: theme.vars.palette.Background.SoftHighlight,
    content: <Habit_PS_5 />,
  },
  {
    type: "habit",
    seqByType: 6,
    hideProgress: true,
    backgroundColor: theme.vars.palette.Background.SoftHighlight,
    content: <Habit_PS_6 />,
  },
  {
    type: "habit",
    seqByType: 7,
    hideProgress: true,
    backgroundColor: theme.vars.palette.Background.SoftHighlight,
    content: <Habit_PS_7 />,
  },
  {
    type: "motivation",
    seqByType: 1,
    title: "의지와 동기",
    progressDelta: 1 / 6,
    content: <Motivation_PS_1 />,
  },
  {
    type: "motivation",
    seqByType: 2,
    title: "의지와 동기",
    progressDelta: 2 / 6,
    content: <Motivation_PS_2 />,
  },
  {
    type: "motivation",
    seqByType: 3,
    title: "의지와 동기",
    progressDelta: 3 / 6,
    content: <Motivation_PS_3 />,
  },
  {
    type: "motivation",
    seqByType: 4,
    hideProgress: true,
    backgroundColor: theme.vars.palette.Background.SoftHighlight,
    content: <Motivation_PS_4 />,
  },
  {
    type: "motivation",
    seqByType: 5,
    title: "의지와 동기",
    progressDelta: 4 / 6,
    content: <Motivation_PS_5 />,
  },
  {
    type: "motivation",
    seqByType: 6,
    hideProgress: true,
    backgroundColor: theme.vars.palette.Background.SoftHighlight,
    content: <Motivation_PS_6 />,
  },
  {
    type: "motivation",
    seqByType: 7,
    title: "의지와 동기",
    progressDelta: 5 / 6,
    content: <Motivation_PS_7_MultiSelect hideNextButton={false} />,
  },
  {
    type: "motivation",
    seqByType: 8,
    hideProgress: true,
    backgroundColor: theme.vars.palette.Background.SoftHighlight,
    hideClose: true,
    content: <Motivation_PS_8 />,
  },
];

function findIndexByStepInfo(
  steps: ProgramSetupStep[],
  type: string,
  seqByType: number
): number {
  return steps.findIndex(
    (step) => step.type === type && step.seqByType === seqByType
  );
}

export default function ProgramSetupScreen_2() {
  const { type, id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useRecoilValue(userAtom);
  const close = useClose();

  const isInappWebview = useIsInappWebview();

  const steps = useMemo(() => {
    return [...commonSteps];
  }, []);

  // const handleBack = () => {
  //   // Determine the previous navigable step, skipping signature steps when signature is absent
  //   let targetIndex = currentStepIndex - 1;
  //
  //   const shouldSkipSignature = !isLoadingCommit && !hasSignature;
  //
  //   while (targetIndex >= 0) {
  //     const step = steps[targetIndex];
  //     const isSignatureStep = step.seq === 0 || step.seq === 14;
  //     if (isSignatureStep && shouldSkipSignature) {
  //       targetIndex -= 1;
  //       continue;
  //     }
  //     break;
  //   }
  //
  //   if (targetIndex < 0) {
  //     if (isInappWebview) {
  //       sendMessage(
  //         JSON.stringify({
  //           event: "navigate_back",
  //         })
  //       );
  //     } else {
  //       navigate("/", { replace: true });
  //       // setPCSideTab("mypage")
  //       // navigate(`/support-faq?fid=101`, { replace: true });
  //     }
  //     return;
  //   }
  //
  //   const prevStep = steps[targetIndex];
  //   navigate(`/refund?seq=${prevStep.seq}`);
  // };

  const numId = parseInt(id || "0");
  const questionNumber = searchParams.get("q");

  const currentStepIndex = Math.min(
    Math.max(findIndexByStepInfo(steps, type!!, numId), 0),
    steps.length - 1
  ); // 유효한 범위 내로 제한

  const currentStep = steps[currentStepIndex];

  const handleBack = () => {
    if (currentStepIndex <= 0) return;
    if (
      (currentStep.type === "convention" || currentStep.type === "habit") &&
      currentStep.seqByType === 2
    ) {
      if (questionNumber !== undefined && parseInt(questionNumber!!) > 1) {
        const prevQuestion = parseInt(questionNumber!!) - 1;
        navigate(
          `/program-setup-2/${currentStep.type}/${currentStep.seqByType}?q=${prevQuestion}`
        );
      } else {
        const prevStep = steps[currentStepIndex - 1];
        navigate(`/program-setup-2/${prevStep.type}/${prevStep.seqByType}`);
      }
      return;
    }

    if (currentStep.type === "convention" && currentStep.seqByType === 3) {
      navigate(
        `/program-setup-2/${currentStep.type}/${currentStep.seqByType - 1}?q=10`
      );
      return;
    }

    if (currentStep.type === "habit" && currentStep.seqByType === 3) {
      navigate(
        `/program-setup-2/${currentStep.type}/${currentStep.seqByType - 1}?q=5`
      );
      return;
    }

    const prevStep = steps[currentStepIndex - 1];
    navigate(`/program-setup-2/${prevStep.type}/${prevStep.seqByType}`);
  };

  const { setProgress, setSectionCount } = useOnboardingProgress();

  useEffect(() => {
    if (currentStep.progressDelta) {
      let ratio = 1;
      if (questionNumber !== undefined && parseInt(questionNumber!!) > 0) {
        ratio = parseInt(questionNumber!!);
      }
      // 2개 섹션, 각 50%
      setSectionCount(3);
      if (currentStep.type === "convention") {
        setProgress(0 + 33 * currentStep.progressDelta * ratio);
      } else if (currentStep.type === "habit") {
        setProgress(33 + 33 * currentStep.progressDelta * ratio);
      } else if (currentStep.type === "motivation") {
        let progress = 66 + 33 * currentStep.progressDelta * ratio;
        setProgress(Math.min(progress, 100)); // 100을 넘지 않도록
      }
    }
  }, [currentStep, questionNumber, setProgress]);

  const setMixPanel = useMixpanelWeb({
    eventName: "온보딩",
    params: {
      type: `${currentStep.type}`,
      pageId: `${currentStep.seqByType}`,
    },
  });

  const { mutate: savePage } = useMutation(
    ({ setupSeq, pageId, params }: { setupSeq: number; pageId: string, params?: string }) =>
      programSetupApi.savePage(setupSeq, pageId, params),
    {
      onSuccess: () => {},
    }
  );

  useEffect(() => {
    if (type === "load") return;
    const paramsString = searchParams.toString();
    savePage({ setupSeq: 2, pageId: `${type}_${id}`, params: paramsString || undefined });
    setMixPanel();
  }, [type, id, searchParams]);

  return currentStep.centerFullScreen === true ? (
    <FullPageWithCenterContentV2
      content={currentStep.content}
      backgroundColor={currentStep.backgroundColor}
    />
  ) : (
    <FullPageWithHeaderAndProgress
      headerTitle={currentStep.title}
      hideHeader={currentStep.hideHeader}
      hideProgress={currentStep.hideProgress}
      paddingXContent={currentStep.paddingXContent}
      body={currentStep.content}
      backgroundColor={currentStep.backgroundColor}
      disableBack={currentStep.disableBack}
      hideBack={currentStep.hideBack}
      onBack={currentStepIndex > 0 ? handleBack : undefined} // 첫 번째 단계에서는 뒤로가기 버튼 숨김
      hideClose={currentStep.hideClose}
      onClose={close}
    />
  );
}
